import { useState, useEffect} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from './firebase';
import {
    Box,
    Modal,
    IconButton,
    Typography,
    Toolbar,
    AppBar,
    CircularProgress,
    Alert,
    Paper,
    Divider,
    Grid,
    Card,
    CardContent,
} from '@mui/material';
import {
    Close as CloseIcon,
    ZoomIn,
    ZoomOut,
    Replay,
    ViewModule,
    ViewStream,
} from '@mui/icons-material';

// PDF.js worker の設定（ローカルファイルを使用）
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    height: '97vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2,
};

const pdfContainerStyle = {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: '8px 8px 0px 8px',
};

export interface PdfViewerModalProps {
    props:{
        open: boolean;
        setOpen: React.Dispatch<React.SetStateAction<boolean>>;
        pdfPath: string;
    };
}

/**
 * PDFプレビュー(モーダル)
 */
const PdfViewerModal: React.FC<PdfViewerModalProps> = ({ props }) => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [title, setTitle] = useState<string>("");
    const [numPages, setNumPages] = useState<number>(0);
    const [scale, setScale] = useState<number>(1.0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [showThumbnails, setShowThumbnails] = useState<boolean>(false);

    // Firebase Storageからダウンロード URL を取得
    useEffect(() => {
        if (props.open && props.pdfPath) {
            setLoading(true);
            setError(null);
            setPdfUrl(null);

            const gsReference = ref(storage, props.pdfPath);
            
            getDownloadURL(gsReference)
                .then((url) => {
                    setPdfUrl(url);
                    const fileNameIndex = props.pdfPath.lastIndexOf("/");
                    const fileName = props.pdfPath.substring(fileNameIndex + 1);
                    setTitle(fileName);
                    setLoading(false);
                })
                .catch((err) => {
                    console.error('Firebase Storage エラー:', err);
                    setError('PDFファイルの取得に失敗しました');
                    setLoading(false);
                });
        }
    }, [props.open, props.pdfPath]);

    // PDF読み込み成功時の処理
    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    // PDF読み込みエラー時の処理
    const onDocumentLoadError = (error: Error) => {
        console.error('PDF読み込みエラー:', error);
        setError('PDFの読み込みに失敗しました');
    };

    // ズーム機能
    const handleZoomIn = () => {
        setScale(prev => Math.min(prev + 0.25, 3.0));
    };

    const handleZoomOut = () => {
        setScale(prev => Math.max(prev - 0.25, 0.5));
    };

    const handleZoomReset = () => {
        setScale(1.0);
    };

    // サムネイル表示切り替え
    const toggleThumbnails = () => {
        setShowThumbnails(prev => !prev);
    };

    // ページ幅の計算
    const getPageWidth = () => {
        const containerWidth = showThumbnails ? 
            Math.min(window.innerWidth * 0.5, 500) : 
            Math.min(window.innerWidth * 0.7, 700);
        return containerWidth * scale;
    };

    // サムネイル用のページ幅
    const getThumbnailWidth = () => {
        return 120;
    };

    // サムネイルクリック時の処理
    const handleThumbnailClick = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        // メインビューの該当ページにスクロール
        const pageElement = document.getElementById(`page-${pageNumber}`);
        if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // 全ページを配列として生成
    const renderAllPages = () => {
        const pages = [];
        for (let i = 1; i <= numPages; i++) {
            pages.push(
                <Box key={i} id={`page-${i}`} sx={{ mb: 2, border: currentPage === i ? '2px solid #1976d2' : 'none' }}>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                        ページ {i} / {numPages}
                    </Typography>
                    <Page
                        pageNumber={i}
                        width={getPageWidth()}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onLoadSuccess={() => {
                            // ページが表示されたら現在のページを更新
                            const observer = new IntersectionObserver(
                                (entries) => {
                                    entries.forEach((entry) => {
                                        if (entry.isIntersecting) {
                                            setCurrentPage(i);
                                        }
                                    });
                                },
                                { threshold: 0.5 }
                            );
                            const element = document.getElementById(`page-${i}`);
                            if (element) {
                                observer.observe(element);
                            }
                        }}
                    />
                </Box>
            );
        }
        return pages;
    };

    // サムネイル表示
    const renderThumbnails = () => {
        const thumbnails = [];
        for (let i = 1; i <= numPages; i++) {
            thumbnails.push(
                <Card 
                    key={i} 
                    sx={{ 
                        mb: 1, 
                        cursor: 'pointer',
                        border: currentPage === i ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        '&:hover': {
                            boxShadow: 3
                        }
                    }}
                    onClick={() => handleThumbnailClick(i)}
                >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                            {i}
                        </Typography>
                        <Page
                            pageNumber={i}
                            width={getThumbnailWidth()}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                        />
                    </CardContent>
                </Card>
            );
        }
        return thumbnails;
    };

    // モーダルを閉じる
    const handleClose = () => {
        setPdfUrl(null);
        setError(null);
        setLoading(false);
        setNumPages(0);
        setScale(1.0);
        setCurrentPage(1);
        setShowThumbnails(false);
        props.setOpen(false);
    };

    return (
        <Modal
            open={props.open}
            onClose={handleClose}
            aria-labelledby="pdf-viewer-modal-title"
        >
            <Box sx={modalStyle}>
                {/* ヘッダー */}
                <AppBar position="static" color="default" elevation={1}>
                    <Toolbar sx={{'@media (min-width:600px)': {minHeight: 'auto'}}}>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            {title}
                        </Typography>

                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* コンテンツエリア */}
                <Box sx={pdfContainerStyle}>
                    {loading && (
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}
                        >
                            <CircularProgress />
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                PDFを読み込み中...
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%'
                            }}
                        >
                            <Alert severity="error" sx={{ maxWidth: 400 }}>
                                {error}
                            </Alert>
                        </Box>
                    )}

                    {!loading && !error && pdfUrl && (
                        <Box>
                            <Document
                                file={pdfUrl}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading={
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                        <CircularProgress />
                                        <Typography sx={{ ml: 2 }}>PDFを読み込み中...</Typography>
                                    </Box>
                                }
                            >
                                {numPages > 0 && (
                                    <Box>
                                        {/* ツールバー */}
                                        <Paper 
                                            elevation={2} 
                                            sx={{ 
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1000,
                                                mb: 2,
                                                marginBottom: '0px'
                                            }}
                                        >
                                            <Toolbar sx={{ justifyContent: 'space-between', '@media (min-width:600px)': {minHeight: 'auto'}}}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <IconButton 
                                                        onClick={toggleThumbnails}
                                                        color={showThumbnails ? 'primary' : 'default'}
                                                    >
                                                        {showThumbnails ? <ViewStream /> : <ViewModule />}
                                                    </IconButton>
                                                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                                    <Typography variant="body2">
                                                        ページ {currentPage} / {numPages}
                                                    </Typography>
                                                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                                    <Typography variant="body2">
                                                        ズーム: {Math.round(scale * 100)}%
                                                    </Typography>
                                                </Box>
                                                
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <IconButton onClick={handleZoomOut} disabled={scale <= 0.5}>
                                                        <ZoomOut />
                                                    </IconButton>
                                                    <IconButton onClick={handleZoomReset}>
                                                        <Replay />
                                                    </IconButton>
                                                    <IconButton onClick={handleZoomIn} disabled={scale >= 3.0}>
                                                        <ZoomIn />
                                                    </IconButton>
                                                </Box>
                                            </Toolbar>
                                        </Paper>

                                        {/* メインコンテンツ */}
                                        <Box sx={{ 
                                            height: 'calc(100% - 80px)', 
                                        }}>
                                            <Grid container>
                                                {/* サムネイル表示 */}
                                                {showThumbnails && (
                                                    <Grid item xs={12} md={2}>
                                                        <Paper 
                                                            elevation={1} 
                                                            sx={{
                                                                p: 1, 
                                                                height: '83vh',
                                                                overflowY: 'auto',
                                                                position: 'sticky',
                                                                top: 0
                                                            }}
                                                        >
                                                            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                                                                サムネイル
                                                            </Typography>
                                                            {renderThumbnails()}
                                                        </Paper>
                                                    </Grid>
                                                )}
                                                
                                                {/* PDFメインビュー */}
                                                <Grid item xs={12} md={showThumbnails ? 10 : 12}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        flexDirection: 'column', 
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        width: '100%',
                                                        height: '83vh',
                                                        overflow: 'auto'
                                                    }}>
                                                        {renderAllPages()}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Box>
                                )}
                            </Document>
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default PdfViewerModal;

// 別タブでPDFを表示
export const handlePdfPreviewScreenOpen = (pdfPathParam: string) => {
    if(pdfPathParam) {
        const gsReference = ref(storage, pdfPathParam);
        const tabTitle = pdfPathParam.split('/').pop();
        
        getDownloadURL(gsReference)
            .then((url) => {
                // HTMLラッパーを作成してPDFを埋め込み
                const htmlContent = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>${tabTitle || 'PDF Viewer'}</title>
                        <meta charset="utf-8">
                        <style>
                            body { 
                                margin: 0; 
                                padding: 0; 
                                font-family: Arial, sans-serif;
                                background-color: #f5f5f5;
                            }
                            .pdf-container {
                                width: 100%;
                                height: 100vh;
                            }
                            iframe { 
                                width: 100%; 
                                height: 100%; 
                                border: none;
                                display: block;
                            }
                            .error-message {
                                text-align: center;
                                padding: 50px;
                                color: #666;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="pdf-container">
                            <iframe src="${url}" type="application/pdf" title="PDF Viewer">
                                <div class="error-message">
                                    <p>PDFを表示できません。</p>
                                </div>
                            </iframe>
                        </div>
                    </body>
                    </html>
                `;
                
                // HTMLコンテンツからBlobを作成し、新しいタブで開く
                const blob = new Blob([htmlContent], { type: 'text/html' });
                const blobUrl = URL.createObjectURL(blob);
                const newWindow = window.open(blobUrl, '_blank');
                
                if (newWindow) {
                    setTimeout(() => {
                        URL.revokeObjectURL(blobUrl);
                    }, 1000);
                }
            })
            .catch((err) => {
                console.error('Firebase Storage エラー:', err);
                // エラー時はエラーページを表示
                const errorHtml = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>エラー - PDF Viewer</title>
                        <meta charset="utf-8">
                        <style>
                            body { 
                                font-family: Arial, sans-serif; 
                                text-align: center; 
                                padding: 50px;
                                background-color: #f5f5f5;
                            }
                            .error-container {
                                background-color: white;
                                padding: 30px;
                                border-radius: 8px;
                                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                                max-width: 500px;
                                margin: 0 auto;
                            }
                            .error-title {
                                color: #d32f2f;
                                font-size: 24px;
                                margin-bottom: 20px;
                            }
                            .error-message {
                                color: #666;
                                font-size: 16px;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="error-container">
                            <div class="error-title">PDFの読み込みに失敗しました</div>
                            <div class="error-message">
                                ファイルが見つからないか、アクセス権限がない可能性があります。<br>
                                しばらく時間をおいてから再度お試しください。
                            </div>
                        </div>
                    </body>
                    </html>
                `;
                const errorBlob = new Blob([errorHtml], { type: 'text/html' });
                const errorBlobUrl = URL.createObjectURL(errorBlob);
                const errorWindow = window.open(errorBlobUrl, '_blank');
                
                if (errorWindow) {
                    setTimeout(() => {
                        URL.revokeObjectURL(errorBlobUrl);
                    }, 1000);
                }
            });
    }
};
